import HeroCanvas from '../components/HeroCanvas.jsx'
import SectionCanvas from '../components/SectionCanvas.jsx'
import { PrismLight as SyntaxHighlighter } from 'react-syntax-highlighter'
import rust from 'react-syntax-highlighter/dist/esm/languages/prism/rust'
import { KARYON_TREE, karyonSrc } from '../links.js'

SyntaxHighlighter.registerLanguage('rust', rust)

const karyonCodeTheme = {
  'pre[class*="language-"]': {
    color: 'var(--text-white)',
    background: 'var(--bg-secondary)',
    fontFamily: 'var(--font-mono)',
  },
  'code[class*="language-"]': {
    color: 'var(--text-white)',
    background: 'transparent',
    fontFamily: 'var(--font-mono)',
  },
  comment: { color: 'var(--text-muted)', fontStyle: 'italic' },
  prolog: { color: 'var(--text-muted)' },
  doctype: { color: 'var(--text-muted)' },
  cdata: { color: 'var(--text-muted)' },
  punctuation: { color: 'var(--text-soft)' },
  property: { color: 'var(--text-white)' },
  tag: { color: 'var(--primary)' },
  boolean: { color: 'var(--primary)' },
  number: { color: 'var(--text-soft)' },
  constant: { color: 'var(--secondary)' },
  symbol: { color: 'var(--secondary)' },
  selector: { color: 'var(--secondary)' },
  'attr-name': { color: 'var(--primary)' },
  string: { color: 'var(--secondary)' },
  char: { color: 'var(--secondary)' },
  builtin: { color: 'var(--secondary)' },
  inserted: { color: 'var(--secondary)' },
  operator: { color: 'var(--text-soft)' },
  entity: { color: 'var(--text-soft)', cursor: 'help' },
  url: { color: 'var(--secondary)' },
  variable: { color: 'var(--text-white)' },
  atrule: { color: 'var(--primary)' },
  'attr-value': { color: 'var(--secondary)' },
  keyword: { color: 'var(--primary)' },
  'class-name': { color: 'var(--secondary)' },
  function: { color: 'var(--text-white)' },
  regex: { color: 'var(--secondary)' },
  important: { color: 'var(--primary)', fontWeight: 'bold' },
  bold: { fontWeight: 'bold' },
  italic: { fontStyle: 'italic' },
  macro: { color: 'var(--primary)' },
  attribute: { color: 'var(--primary)' },
  'lifetime-annotation': { color: 'var(--text-soft)' },
  'fragment-specifier': { color: 'var(--secondary)' },
  'closure-params': { color: 'var(--text-soft)' },
}

const codeBlockStyle = {
  background: 'var(--bg-secondary)',
  border: '1px solid var(--border)',
  borderRadius: 'var(--radius)',
  padding: '1.5rem',
  fontSize: '0.85rem',
  margin: 0,
  fontFamily: 'var(--font-mono)',
  overflowX: 'auto',
  maxWidth: '100%',
}


function RustBlock({ children }) {
  return (
    <SyntaxHighlighter
      language="rust"
      style={karyonCodeTheme}
      customStyle={codeBlockStyle}
      codeTagProps={{ style: { fontFamily: 'var(--font-mono)' } }}
    >
      {children}
    </SyntaxHighlighter>
  )
}

const jsonrpcServer = `use std::sync::Arc;
use serde_json::Value;
use karyon_jsonrpc::{
    error::RPCError, rpc_impl, server::ServerBuilder,
};

struct Calc {}

#[rpc_impl]
impl Calc {
    async fn add(&self, params: Value) -> Result<Value, RPCError> {
        let (a, b): (i32, i32) = serde_json::from_value(params)?;
        Ok(serde_json::json!(a + b))
    }
}

let service = Arc::new(Calc {});

let server = ServerBuilder::new("tcp://127.0.0.1:60000")?
    .service(service)
    .build()
    .await?;

server.start_block().await?;`

const jsonrpcClient = `use karyon_jsonrpc::client::ClientBuilder;

let client = ClientBuilder::new("tcp://127.0.0.1:60000")?
    .build()
    .await?;

let result: i32 = client
    .call("Calc.add", (1, 2))
    .await?;`

const protocolExample = `pub struct MyProtocol {
    peer: PeerConn,
}

impl MyProtocol {
    fn new(peer: PeerConn) -> Self {
        Self { peer }
    }
}

#[async_trait]
impl Protocol for MyProtocol {
    async fn start(self: Arc<Self>) -> Result<(), Error> {
        loop {
            match self.peer.recv().await {
                Ok(msg) => { /* handle */ }
                Err(Error::PeerShutdown) => break,
                Err(e) => return Err(e),
            }
        }
        Ok(())
    }

    fn version() -> Result<Version, Error> {
        "0.1.0, >0.1.0".parse()
    }

    fn id() -> ProtocolID {
        "MYPROTO".into()
    }
}

node.attach_protocol(MyProtocol::new).await?;`

export default function KaryonPage() {
  return (
    <>
      <section className="subpage-hero slope-bottom">
        <HeroCanvas />
        <div className="subpage-hero-content">
          <h1>Karyon</h1>
          <p>A set of composable Rust crates for networking, peer-to-peer systems, and decentralized software.</p>
        </div>
      </section>

      <div className="subpage-content">
        <div className="sp-overview">
          <div className="sp-overview-text">
            <p><code>karyon_net</code> provides a layered async networking stack with composable transports and middleware, supporting TCP, QUIC, UDP, Unix sockets, TLS, WebSocket, and SOCKS5 proxying.</p>
            <p><code>karyon_jsonrpc</code> offers a lightweight async JSON-RPC 2.0 implementation with support for stream transports, QUIC multiplexing, and native HTTP/1.1, HTTP/2, and HTTP/3 communication.</p>
            <p><code>karyon_p2p</code> delivers an extensible peer-to-peer stack with pluggable discovery, negotiated protocols, secure peer identity, and multi-endpoint connectivity over TCP, TLS, and QUIC.</p>
            <p><code>karyon_swarm</code> builds on the p2p layer with protocol-aware swarms and BitTorrent-style swarm-keyed discovery for scalable decentralized peer coordination.</p>
            <p>All crates support both <strong>smol</strong> (default) and <strong>tokio</strong>. To use tokio, disable default features and enable the <code>tokio</code> feature.</p>
            <div className="sp-doc-link">
              <a href={KARYON_TREE} className="btn btn-outline btn-sm">Source Code</a>
            </div>
          </div>
          <div className="sp-overview-meta">
            <dl>
              <dt>Language</dt><dd>Rust</dd>
              <dt>License</dt><dd>MIT</dd>
              <dt>Async Runtime</dt><dd>smol / tokio</dd>
            </dl>
          </div>
        </div>
      </div>

      <div className="subpage-band-alt">
        <div className="subpage-content">
          {/* Use Cases */}
          <div className="sp-section" id="use-cases">
          <span className="section-eyebrow">01</span>
          <h2><a href="#use-cases" className="section-anchor">Use Cases<span className="dot">.</span></a></h2>
          <div className="sp-grid">
            <div className="sp-card">
              <h3>Decentralized AI Inference</h3>
              <p>Distributed model serving across a peer network, routing requests to nodes hosting the weights.</p>
            </div>
            <div className="sp-card">
              <h3>AI Edge Computing</h3>
              <p>AI workloads running on edge devices, coordinated peer-to-peer without round-trips to a central cloud.</p>
            </div>
            <div className="sp-card">
              <h3>Federated Learning</h3>
              <p>Coordinated model training across peers, exchanging gradients without centralizing the data.</p>
            </div>
            <div className="sp-card">
              <h3>Local-First Applications</h3>
              <p>Software that keeps data on-device and syncs directly between user devices over peer-to-peer connections.</p>
            </div>
            <div className="sp-card">
              <h3>Distributed Databases</h3>
              <p>Replicated state across peers using custom consensus.</p>
            </div>
            <div className="sp-card">
              <h3>Custom Protocols</h3>
              <p>Application-specific protocols implemented against the <code>Protocol</code> trait, with framing and lifecycle.</p>
            </div>
          </div>
        </div>
        </div>
      </div>

      {/* Crates */}
      <section className="sp-band sp-band-alt slope-top slope-bottom" id="crates">
        <SectionCanvas variant="network" />
        <div className="sp-band-inner">
          <span className="section-eyebrow">02</span>
          <h2><a href="#crates" className="section-anchor">Crates<span className="dot">.</span></a></h2>
          <p className="sp-band-sub">Independently versioned crates that compose into a complete decentralized stack.</p>
          <div className="sp-grid">
            <div className="sp-card">
              <h3>karyon_core</h3>
              <p>Core utilities and shared foundational components.</p>
              <div className="sp-card-links">
                <a href={karyonSrc('core')} className="sp-card-link">Source <span className="sp-card-link-arrow">↗</span></a>
                <a href="https://docs.rs/karyon_core/latest/karyon_core/" className="sp-card-link">API Docs <span className="sp-card-link-arrow">↗</span></a>
              </div>
            </div>
            <div className="sp-card">
              <h3>karyon_net</h3>
              <p>Layered networking stack with composable transports and middleware, including TCP, QUIC, Unix sockets, TLS, WebSocket, SOCKS5, and UDP.</p>
              <div className="sp-card-links">
                <a href={karyonSrc('net')} className="sp-card-link">Source <span className="sp-card-link-arrow">↗</span></a>
                <a href="https://docs.rs/karyon_net/latest/karyon_net/" className="sp-card-link">API Docs <span className="sp-card-link-arrow">↗</span></a>
              </div>
            </div>
            <div className="sp-card">
              <h3>karyon_p2p</h3>
              <p>Extensible peer-to-peer networking stack with pluggable discovery, negotiated protocols, and multi-endpoint connectivity over TCP, TLS, and QUIC.</p>
              <div className="sp-card-links">
                <a href={karyonSrc('p2p')} className="sp-card-link">Source <span className="sp-card-link-arrow">↗</span></a>
                <a href="https://docs.rs/karyon_p2p/latest/karyon_p2p/" className="sp-card-link">API Docs <span className="sp-card-link-arrow">↗</span></a>
              </div>
            </div>
            <div className="sp-card">
              <h3>karyon_jsonrpc</h3>
              <p>Lightweight async JSON-RPC 2.0 framework supporting TCP, TLS, WebSocket, QUIC, HTTP/1.1, HTTP/2, HTTP/3, and Unix transports.</p>
              <div className="sp-card-links">
                <a href={karyonSrc('jsonrpc')} className="sp-card-link">Source <span className="sp-card-link-arrow">↗</span></a>
                <a href="https://docs.rs/karyon_jsonrpc/latest/karyon_jsonrpc/" className="sp-card-link">API Docs <span className="sp-card-link-arrow">↗</span></a>
              </div>
            </div>
            <div className="sp-card">
              <h3>karyon_swarm</h3>
              <p>Builds on the p2p layer with protocol-aware swarms and BitTorrent-style swarm-keyed discovery for scalable decentralized peer coordination.</p>
              <div className="sp-card-links">
                <a href={karyonSrc('swarm')} className="sp-card-link">Source <span className="sp-card-link-arrow">↗</span></a>
                <a href="https://docs.rs/karyon_swarm/latest/karyon_swarm/" className="sp-card-link">API Docs <span className="sp-card-link-arrow">↗</span></a>
              </div>
            </div>
            <div className="sp-card">
              <h3>karyon_eventemitter</h3>
              <p>Lightweight asynchronous event emitter for strongly typed pub/sub communication.</p>
              <div className="sp-card-links">
                <a href={karyonSrc('utils/eventemitter')} className="sp-card-link">Source <span className="sp-card-link-arrow">↗</span></a>
                <a href="https://docs.rs/karyon_eventemitter/latest/karyon_eventemitter/" className="sp-card-link">API Docs <span className="sp-card-link-arrow">↗</span></a>
              </div>
            </div>
          </div>
        </div>
      </section>

      <div className="subpage-content">
        {/* P2P Architecture */}
        <div className="sp-section" id="p2p-architecture">
          <span className="section-eyebrow">03</span>
          <h2><a href="#p2p-architecture" className="section-anchor">P2P Architecture<span className="dot">.</span></a></h2>
          <p>How <code>karyon_p2p</code> handles transport, discovery, and protocols.</p>
          <div className="arch-timeline">
            <div className="arch-item">
              <div className="arch-marker"></div>
              <div className="arch-content">
                <h3>Multi-Transport</h3>
                <p>Peers listen and connect over multiple transports simultaneously: TCP, TLS, and QUIC. Configuration uses endpoint URLs (<code>tcp://</code>, <code>tls://</code>, <code>quic://</code>). The connector picks the best mutually supported transport by priority.</p>
              </div>
            </div>
            <div className="arch-item">
              <div className="arch-marker"></div>
              <div className="arch-content">
                <h3>Pluggable Discovery</h3>
                <p>Discovery is abstracted behind a <code>Discovery</code> trait. Implementations find peers and emit <code>DiscoveredPeer</code> values through a channel; the Backend consumes them and handles connecting. New discovery mechanisms (mDNS, gossip, static peer lists) can be added without touching the connection layer.</p>
              </div>
            </div>
            <div className="arch-item">
              <div className="arch-marker"></div>
              <div className="arch-content">
                <h3>Kademlia DHT</h3>
                <p>The default discovery uses a customized Kademlia DHT with TCP/TLS (or QUIC when the <code>quic</code> feature is enabled) for lookup, and UDP for routing table refresh.</p>
              </div>
            </div>
            <div className="arch-item">
              <div className="arch-marker"></div>
              <div className="arch-content">
                <h3>Protocols</h3>
                <p>A built-in <code>Ping</code> keep-alive runs on every connection; everything else is a custom protocol. For TCP/TLS, protocols share a single framed connection. For QUIC, each protocol gets its own bidirectional stream.</p>
                <p><code>Protocol::flags()</code> defaults to <code>ProtocolFlags::PREFERRED</code>. Override to <code>ProtocolFlags::REQUIRED</code> for protocols every peer must speak, or <code>ProtocolFlags::empty()</code> for protocols that should not be advertised.</p>
              </div>
            </div>
            <div className="arch-item">
              <div className="arch-marker"></div>
              <div className="arch-content">
                <h3>Network Security</h3>
                <p>TLS is available for TCP connections. QUIC has built-in TLS 1.3. The p2p layer generates self-signed certificates from the node's key pair for mutual authentication.</p>
              </div>
            </div>
            <div className="arch-item">
              <div className="arch-marker"></div>
              <div className="arch-content">
                <h3>Peer Identity</h3>
                <p>Each peer is identified by a <code>PeerID</code> derived from its key pair. When a connection is established, the client initiates a handshake; if successful, the connection enters the <code>PeerPool</code>.</p>
              </div>
            </div>
            <div className="arch-item">
              <div className="arch-marker"></div>
              <div className="arch-content">
                <h3>Access Control</h3>
                <p>A pluggable <code>AccessControl</code> policy decides which peers the node talks to: endpoints are checked before the handshake, peer identities after it. Custom policies enable private or semi-private deployments with any discovery backend.</p>
              </div>
            </div>
            <div className="arch-item">
              <div className="arch-marker"></div>
              <div className="arch-content">
                <h3>Swarms</h3>
                <p>Protocol-aware peer groups with BitTorrent-style swarm-keyed discovery. <code>SwarmKey</code> defaults to <code>hash(ProtocolID)</code>, with an optional instance name (e.g. <code>"general"</code>) for sub-grouping into rooms or topics. Peers are auto-assigned to the swarms they support.</p>
              </div>
            </div>
          </div>
        </div>

        {/* Custom Protocol */}
        <div className="sp-section" id="custom-protocols">
          <span className="section-eyebrow">04</span>
          <h2><a href="#custom-protocols" className="section-anchor">Custom Protocols<span className="dot">.</span></a></h2>
          <p>Implement the <code>Protocol</code> trait to add application-level logic to any Karyon peer.</p>
          <RustBlock>{protocolExample}</RustBlock>
        </div>
      </div>

      <div className="subpage-content">
        {/* JSON-RPC */}
        <div className="sp-section" id="jsonrpc">
          <span className="section-eyebrow">05</span>
          <h2><a href="#jsonrpc" className="section-anchor">JSON-RPC<span className="dot">.</span></a></h2>
          <p>A minimal <code>karyon_jsonrpc</code> server and client over TCP.</p>
          <RustBlock>{jsonrpcServer}</RustBlock>
          <p style={{ marginTop: '1.5rem' }}>Calling the server from a client:</p>
          <RustBlock>{jsonrpcClient}</RustBlock>
          <div className="sp-doc-link">
            <a href="https://docs.rs/karyon_jsonrpc" className="btn btn-outline btn-sm">Read the Docs</a>
          </div>
        </div>
      </div>

      {/* Roadmap */}
      <section className="sp-band sp-band-alt slope-top slope-bottom" id="roadmap">
        <SectionCanvas variant="network" />
        <div className="sp-band-inner">
          <span className="section-eyebrow">06</span>
          <h2><a href="#roadmap" className="section-anchor">Roadmap<span className="dot">.</span></a></h2>
          <p className="sp-band-sub">Planned features beyond the initial 1.0 release.</p>
          <div className="roadmap-timeline">
            <div className="roadmap-item">
              <span className="roadmap-index"></span>
              <div className="roadmap-body">
                <h3>mDNS Discovery</h3>
                <p>Local network discovery via an mDNS implementation of the <code>Discovery</code> trait.</p>
              </div>
              <span className="roadmap-status">Planned</span>
            </div>
            <div className="roadmap-item">
              <span className="roadmap-index"></span>
              <div className="roadmap-body">
                <h3>karyon_stun</h3>
                <p>Minimal STUN server and client. Multi-transport (UDP primary, plus TCP, TLS, QUIC) built on <code>karyon_net</code>.</p>
              </div>
              <span className="roadmap-status">Planned</span>
            </div>
            <div className="roadmap-item">
              <span className="roadmap-index"></span>
              <div className="roadmap-body">
                <h3>karyon_pow</h3>
                <p>Proof-of-work gate for inbound connections, using Equi-X (the same puzzle algorithm as Tor / Arti).</p>
              </div>
              <span className="roadmap-status">Planned</span>
            </div>
          </div>
        </div>
      </section>
    </>
  )
}
